import { useTranslation } from 'react-i18next';
import { Card, Divider, Flex, Grid, Loader, Text, Title } from '@mantine/core';
import { useLocation } from 'react-router-dom';
import { AreaChart } from '@mantine/charts';
import { useGetMonthSalesStatisticsReport } from '../../../hooks/reports/queries';
import momentTz from 'moment-timezone';
import { CommonReportChartProps } from '.';
import moment from 'moment-timezone';
import { formatMoneyString } from '../../../utils/strings';

const MonthSalesChart = ({ dateRange, chartHeight }: CommonReportChartProps) => {
  const { t } = useTranslation(['ReportsPage', 'FieldName', 'CommonButtons', 'Notifications']);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const searchParamStoreId = searchParams.get('storeId') || '';

  const { data, isLoading } = useGetMonthSalesStatisticsReport(searchParamStoreId, {
    fromDate: dateRange[0]?.toISOString(),
    toDate: dateRange[1]?.toISOString(),
    timezone: momentTz.tz.guess(true)
  });

  return (
    <Card h="100%">
      <Card.Section inheritPadding withBorder py="md">
        <Grid>
          <Grid.Col span={12}>
            <Title>{t('ReportsPage:monthSalesReport.title')}</Title>
            <Text c={'dimmed'}>
              {moment(dateRange[0]).format('DD MMMM YYYY')} -{' '}
              {moment(dateRange[1]).format('DD MMMM YYYY')}
            </Text>
          </Grid.Col>
          {isLoading && <Loader m={'auto'} />}
          {!isLoading && (
            <>
              <Grid.Col>
                <AreaChart
                  h={chartHeight}
                  data={Object.keys(data?.monthReport?.orders || {})?.map((dayKey: string) => {
                    const sale = data?.monthReport?.orders[dayKey];
                    return {
                      salesDate: dayKey,
                      totalSale: sale
                    };
                  })}
                  dataKey="salesDate"
                  series={[{ name: 'totalSale', color: '#4CAF50', label: t('FieldName:total') }]}
                  curveType="linear"
                />
              </Grid.Col>
              <Grid.Col span={12}>
                <Divider size="sm" my="xs" />
                <Flex justify="space-between" align="center">
                  <Text>{t('ReportsPage:monthSalesReport.sales')}</Text>
                  <Text>{formatMoneyString((data?.monthReport.sales || 0).toFixed(2))}</Text>
                </Flex>
                <Divider size="sm" my="xs" />
                <Flex justify="space-between" align="center">
                  <Text>{t('ReportsPage:monthSalesReport.unpaid')}</Text>
                  <Text>{formatMoneyString((data?.monthReport.unpaid || 0).toFixed(2))}</Text>
                </Flex>
                <Divider size="sm" my="xs" />
                <Flex justify="space-between" align="center">
                  <Text>{t('ReportsPage:monthSalesReport.ordersCount')}</Text>
                  <Text>{data?.monthReport.ordersCount || 0}</Text>
                </Flex>
                <Divider size="sm" my="xs" />
                <Flex justify="space-between" align="center">
                  <Text>{t('ReportsPage:monthSalesReport.collectedOrders')}</Text>
                  <Text>{data?.monthReport.collectedOrders || 0}</Text>
                </Flex>
                <Divider size="sm" my="xs" />
                <Flex justify="space-between" align="center">
                  <Text>{t('ReportsPage:monthSalesReport.cancelledOrders')}</Text>
                  <Text>{data?.monthReport.cancelledOrders || 0}</Text>
                </Flex>
              </Grid.Col>
              <Grid.Col span={12}>
                {!Object.keys(data?.monthReport?.orders || {})?.length && (
                  <Text ta={'center'}>{t('ReportsPage:noData')}</Text>
                )}
              </Grid.Col>
            </>
          )}
        </Grid>
      </Card.Section>
    </Card>
  );
};

export default MonthSalesChart;
